import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle, X, RefreshCw, Search } from 'lucide-react';
import { buildApiUrl } from '../config';

interface DomainUsage {
  _id: string;
  domain: string;
  buildId: string;
  origin: string;
  ipAddress?: string;
  userAgent?: string;
  firstSeen: string;
  lastSeen: string;
  count: number;
  isAuthorized: boolean;
}

const DomainTracking: React.FC = () => {
  const navigate = useNavigate();
  const [domains, setDomains] = useState<DomainUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unauthorized' | 'authorized'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, authorized: 0, unauthorized: 0 });

  useEffect(() => {
    if (!localStorage.getItem('developer_token')) {
      navigate('/developer');
      return;
    }
    loadDomains();
  }, [navigate, filter]);

  const loadDomains = async () => {
    setLoading(true);
    try {
      const authorized = filter === 'all' ? undefined : filter === 'authorized';
      const params = new URLSearchParams();
      if (authorized !== undefined) params.append('authorized', String(authorized));
      if (searchTerm) params.append('search', searchTerm);
      
        const response = await fetch(
          buildApiUrl(`/api/analytics/domains?${params}`),
          {
            headers: {
              'x-admin-token': localStorage.getItem('developer_token') || '',
            },
          }
        );
      
      if (response.ok) {
        const data = await response.json();
        setDomains(data.items);
        updateStats(data.items);
      }
    } catch (error) {
      console.error('Failed to load domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (items: DomainUsage[]) => {
    const total = items.length;
    const authorized = items.filter(d => d.isAuthorized).length;
    const unauthorized = total - authorized;
    setStats({ total, authorized, unauthorized });
  };

  const toggleAuthorization = async (id: string, currentStatus: boolean) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/analytics/domains/${id}/authorize`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-token': localStorage.getItem('developer_token') || '',
          },
          body: JSON.stringify({ isAuthorized: !currentStatus }),
        }
      );

      if (response.ok) {
        loadDomains();
      }
    } catch (error) {
      console.error('Failed to update authorization:', error);
    }
  };

  const deleteDomain = async (id: string) => {
    if (!window.confirm('Delete this domain record?')) return;
    
    try {
      const response = await fetch(
        buildApiUrl(`/api/analytics/domains/${id}`),
        {
          method: 'DELETE',
          headers: {
            'x-admin-token': localStorage.getItem('developer_token') || '',
          },
        }
      );

      if (response.ok) {
        loadDomains();
      }
    } catch (error) {
      console.error('Failed to delete domain:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Domain Tracking</h1>
          <p className="text-gray-600">Monitor unauthorized usage of your application</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Domains</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Shield className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Authorized</p>
                <p className="text-2xl font-bold text-green-600">{stats.authorized}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unauthorized</p>
                <p className="text-2xl font-bold text-red-600">{stats.unauthorized}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search domains..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loadDomains()}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === 'all' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('authorized')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === 'authorized' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Authorized
              </button>
              <button
                onClick={() => setFilter('unauthorized')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === 'unauthorized' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Unauthorized
              </button>
              <button
                onClick={loadDomains}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Domains Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Domain
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Build ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    First Seen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Seen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      Loading domains...
                    </td>
                  </tr>
                ) : domains.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      No domains found
                    </td>
                  </tr>
                ) : (
                  domains.map((domain) => (
                    <tr key={domain._id} className={!domain.isAuthorized ? 'bg-red-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{domain.domain}</div>
                          <div className="text-sm text-gray-500">{domain.origin}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {domain.buildId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(domain.firstSeen)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(domain.lastSeen)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {domain.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            domain.isAuthorized
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {domain.isAuthorized ? 'Authorized' : 'Unauthorized'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => toggleAuthorization(domain._id, domain.isAuthorized)}
                            className={`${
                              domain.isAuthorized
                                ? 'text-orange-600 hover:text-orange-900'
                                : 'text-green-600 hover:text-green-900'
                            }`}
                            title={domain.isAuthorized ? 'Mark as Unauthorized' : 'Mark as Authorized'}
                          >
                            {domain.isAuthorized ? 'Unauthorize' : 'Authorize'}
                          </button>
                          <button
                            onClick={() => deleteDomain(domain._id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 text-sm text-gray-600">
          <p>
            <strong>How it works:</strong> When your application loads, it automatically sends a beacon to track which
            domain it's running on. Unauthorized domains appear in red. You can authorize legitimate domains or investigate
            suspicious ones.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DomainTracking;
